import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { memoryService, MemoryPlace, MemoryConnection } from '../../services/memoryService';
import PageHeader from '../common/PageHeader';
import PlaceForm from './PlaceForm';
import MemoryPalaceGraph from './MemoryPalaceGraph';
import './MemoryPalace.css';

interface MemoryPalaceProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type ViewMode = 'list' | 'graph' | 'root';

const MemoryPalace: React.FC<MemoryPalaceProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [places, setPlaces] = useState<MemoryPlace[]>([]);
  const [connections, setConnections] = useState<MemoryConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('root');
  const [showPlaceForm, setShowPlaceForm] = useState(false);
  const [editingPlace, setEditingPlace] = useState<MemoryPlace | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [openItemMenu, setOpenItemMenu] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNoConnectionsOnly, setShowNoConnectionsOnly] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemMenuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user]);

  // Check for create param from quick command
  useEffect(() => {
    const createParam = searchParams.get('create');
    if (createParam === 'true') {
      setEditingPlace(null);
      setShowPlaceForm(true);
      // Remove the param from URL
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
      if (openItemMenu !== null) {
        const menuElement = itemMenuRefs.current[openItemMenu];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenItemMenu(null);
        }
      }
    };

    if (showMenu || openItemMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, openItemMenu]);

  const loadData = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const [placesResponse, connectionsResponse] = await Promise.all([
        memoryService.getUserPlaces(user.id),
        memoryService.getUserConnections(user.id)
      ]);

      if (placesResponse.success && placesResponse.places) {
        setPlaces(placesResponse.places);
      }

      if (connectionsResponse.success && connectionsResponse.connections) {
        setConnections(connectionsResponse.connections);
      }
    } catch (err) {
      console.error('Error loading memory palace data:', err);
      setError('Failed to load memory palace data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlace = async (placeData: { name: string; description?: string; properties?: Record<string, any> | string; is_root?: boolean }) => {
    if (!user?.id) return;

    try {
      setError(null);
      const response = await memoryService.createPlace({
        user_id: user.id,
        ...placeData
      });

      if (response.success) {
        await loadData();
        setShowPlaceForm(false);
      } else {
        setError(response.message || 'Failed to create place');
      }
    } catch (err) {
      console.error('Error creating place:', err);
      setError('Failed to create place');
    }
  };

  const handleDeletePlace = async (placeId: number) => {
    if (!window.confirm('Are you sure you want to delete this place? All connections will be removed.')) {
      return;
    }

    try {
      setError(null);
      const response = await memoryService.deletePlace(placeId);

      if (response.success) {
        await loadData();
      } else {
        setError(response.message || 'Failed to delete place');
      }
    } catch (err) {
      console.error('Error deleting place:', err);
      setError('Failed to delete place');
    }
  };

  const handleMarkAsRoot = async (placeId: number) => {
    if (!user?.id) return;

    try {
      setError(null);
      const response = await memoryService.updatePlace(placeId, { is_root: true });

      if (response.success) {
        await loadData();
        setOpenItemMenu(null);
      } else {
        setError(response.message || 'Failed to mark place as root');
      }
    } catch (err) {
      console.error('Error marking place as root:', err);
      setError('Failed to mark place as root');
    }
  };

  const handlePlaceClick = (placeId: number) => {
    navigate(`/memory/${placeId}`);
  };

  const getPlaceConnectionCount = (placeId: number): number => {
    return connections.filter(
      conn => conn.from_place_id === placeId || conn.to_place_id === placeId
    ).length;
  };

  // Filter data for root view
  const getRootViewData = () => {
    const rootPlaces = places.filter(place => place.is_root);
    const rootPlaceIds = new Set(rootPlaces.map(place => place.id));
    
    // Find all connections involving root places
    const rootConnections = connections.filter(
      conn => rootPlaceIds.has(conn.from_place_id) || rootPlaceIds.has(conn.to_place_id)
    );
    
    // Collect all place IDs that are connected to root places
    const connectedPlaceIds = new Set<number>();
    rootConnections.forEach(conn => {
      connectedPlaceIds.add(conn.from_place_id);
      connectedPlaceIds.add(conn.to_place_id);
    });
    
    // Include all places that are either root or connected to root places
    const filteredPlaces = places.filter(place => connectedPlaceIds.has(place.id));
    
    return { places: filteredPlaces, connections: rootConnections };
  };

  // Get filtered data based on view mode
  const getFilteredData = () => {
    let filteredPlaces = places;
    let filteredConnections = connections;
    
    if (viewMode === 'root') {
      const rootData = getRootViewData();
      filteredPlaces = rootData.places;
      filteredConnections = rootData.connections;
    }
    
    // Apply no connections filter if active
    if (showNoConnectionsOnly) {
      filteredPlaces = filteredPlaces.filter(place => getPlaceConnectionCount(place.id) === 0);
      filteredConnections = [];
    }
    
    return { places: filteredPlaces, connections: filteredConnections };
  };

  const truncateDescription = (description?: string, maxLength: number = 100): string => {
    if (!description) return '';
    return description.length > maxLength 
      ? description.substring(0, maxLength) + '...' 
      : description;
  };

  // Filter places based on search query
  const getFilteredPlaces = (placesToFilter: MemoryPlace[]): MemoryPlace[] => {
    let filtered = placesToFilter;
    
    // Apply no connections filter if active
    if (showNoConnectionsOnly) {
      filtered = filtered.filter(place => getPlaceConnectionCount(place.id) === 0);
    }
    
    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(place => {
        const nameMatch = place.name.toLowerCase().includes(query);
        const descriptionMatch = place.description?.toLowerCase().includes(query);
        return nameMatch || descriptionMatch;
      });
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="memory-palace-container">
        <div className="memory-palace-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="memory-palace-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">/memory</h1>}
        centerClassName="memory-palace-title"
        rightActions={
          <div className="memory-palace-menu-container" ref={menuRef}>
            <button
              className="memory-palace-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="memory-palace-menu-dropdown">
                <button
                  className={`memory-palace-menu-item ${viewMode === 'root' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('root');
                    setShowMenu(false);
                  }}
                >
                  Root View
                </button>
                <button
                  className={`memory-palace-menu-item ${viewMode === 'list' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('list');
                    setShowMenu(false);
                  }}
                >
                  List View
                </button>
                <button
                  className={`memory-palace-menu-item ${viewMode === 'graph' ? 'active' : ''}`}
                  onClick={() => {
                    setViewMode('graph');
                    setShowMenu(false);
                  }}
                >
                  Graph View
                </button>
                <div className="memory-palace-menu-separator"></div>
                <button
                  className={`memory-palace-menu-item ${showNoConnectionsOnly ? 'active' : ''}`}
                  onClick={() => {
                    setShowNoConnectionsOnly(!showNoConnectionsOnly);
                    setShowMenu(false);
                  }}
                >
                  Places with No Connections
                </button>
                <button
                  className="memory-palace-menu-item"
                  onClick={() => {
                    setEditingPlace(null);
                    setShowPlaceForm(true);
                    setShowMenu(false);
                  }}
                >
                  + Add Place
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="memory-palace-error">{error}</div>
      )}

      {showPlaceForm && (
        <PlaceForm
          place={editingPlace}
          onSave={handleCreatePlace}
          onCancel={() => {
            setShowPlaceForm(false);
            setEditingPlace(null);
          }}
        />
      )}

      {viewMode === 'list' ? (
        <div className="memory-palace-list">
          {places.length > 0 && (
            <div className="memory-palace-search-container">
              <input
                type="text"
                className="memory-palace-search-input"
                placeholder="Search places..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}
          {places.length === 0 ? (
            <div className="memory-palace-empty">
              <p>No places yet. Create your first place to start building your memory palace.</p>
            </div>
          ) : (
            <div className="memory-palace-list-items">
              {getFilteredPlaces(places).length === 0 ? (
                <div className="memory-palace-empty">
                  <p>
                    {showNoConnectionsOnly && searchQuery.trim()
                      ? `No places with no connections found matching "${searchQuery}".`
                      : showNoConnectionsOnly
                      ? 'No places with no connections.'
                      : searchQuery.trim()
                      ? `No places found matching "${searchQuery}".`
                      : 'No places found.'}
                  </p>
                </div>
              ) : (
                getFilteredPlaces(places).map((place) => (
                <div
                  key={place.id}
                  className="memory-palace-item"
                  onClick={() => handlePlaceClick(place.id)}
                >
                  <div className="memory-palace-item-header">
                    <span className="memory-palace-item-name">
                      /{place.name}
                      {place.is_root && <span className="memory-palace-root-badge">root</span>}
                    </span>
                    <div className="memory-palace-item-actions">
                      <div 
                        className="memory-palace-menu-container"
                        ref={(el) => { itemMenuRefs.current[place.id] = el; }}
                      >
                        <button
                          className="memory-palace-menu-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenItemMenu(openItemMenu === place.id ? null : place.id);
                          }}
                          title="Menu"
                        >
                          ⋮
                        </button>
                        {openItemMenu === place.id && (
                          <div className="memory-palace-menu-dropdown">
                            {!place.is_root && (
                              <button
                                className="memory-palace-menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRoot(place.id);
                                }}
                              >
                                Mark as Root
                              </button>
                            )}
                            <button
                              className="memory-palace-menu-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePlace(place.id);
                                setOpenItemMenu(null);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {place.description && (
                    <div className="memory-palace-item-description">
                      {truncateDescription(place.description)}
                    </div>
                  )}
                  <div className="memory-palace-item-meta">
                    <span className="memory-palace-item-connections">
                      {getPlaceConnectionCount(place.id)} connection{getPlaceConnectionCount(place.id) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                ))
              )}
            </div>
          )}
        </div>
      ) : (
        <MemoryPalaceGraph
          places={getFilteredData().places}
          connections={getFilteredData().connections}
          onPlaceClick={handlePlaceClick}
        />
      )}
    </div>
  );
};

export default MemoryPalace;

